import { useCallback, useEffect, useRef, useState } from "react";

// Dwell time the pointer must stay on the trigger before the card opens, so a quick
// pass-through (e.g. scrolling past a row) doesn't pop the card.
const OPEN_DELAY_MS = 600;

// Grace period before the interactive tooltip closes, so the pointer can cross the
// gap between the trigger and the popper (and back) without dismissing it.
const CLOSE_DELAY_MS = 120;

interface UseHoverIntentOptions {
  /**
   * Element that triggers the popover on hover; the hover listeners attach to it.
   * `null` while the anchor is still being resolved (e.g. a child ref hasn't attached
   * yet), in which case the hook is inert.
   */
  anchorElement: HTMLElement | null;
  /**
   * Force the anchor to `display: inline-block`. Use for an externally-owned host element
   * (e.g. a web-component portal) that needs a layout box; leave off when the anchor is a
   * React child that already controls its own display.
   */
  forceInlineBlock?: boolean;
  /** Dwell time on the trigger before opening. */
  openDelayMs?: number;
  /** Grace period before closing once the pointer has left both trigger and popper. */
  closeDelayMs?: number;
}

export interface HoverIntent {
  /** Whether the popover is currently open. */
  open: boolean;
  /** True once the popover has opened at least once (latch for lazy data loads). */
  hasOpened: boolean;
  /** Open immediately, cancelling any pending close. Wire to the popper's `onMouseEnter`. */
  openNow: () => void;
  /** Schedule a close after the grace period. Wire to the popper's `onMouseLeave`. */
  scheduleClose: () => void;
}

/**
 * Hover-intent state machine for an interactive popover anchored to `anchorElement`.
 *
 * Opens only after the pointer dwells on the trigger for `openDelayMs` (a quick
 * pass-through won't pop it) and closes after a `closeDelayMs` grace period so the
 * pointer can cross the gap between trigger and popper without dismissing it. The
 * popper should call `openNow`/`scheduleClose` on its own mouse enter/leave so moving
 * onto the (already open) card keeps it open without re-waiting the open delay.
 */
export const useHoverIntent = ({
  anchorElement,
  forceInlineBlock = false,
  openDelayMs = OPEN_DELAY_MS,
  closeDelayMs = CLOSE_DELAY_MS,
}: UseHoverIntentOptions): HoverIntent => {
  const [open, setOpen] = useState(false);
  // One-way latch: once opened, callers keep the query active so re-opens hit the cache.
  const [hasOpened, setHasOpened] = useState(false);
  const openTimerRef = useRef<number | undefined>(undefined);
  const closeTimerRef = useRef<number | undefined>(undefined);

  const cancelScheduledOpen = useCallback(() => {
    if (openTimerRef.current !== undefined) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = undefined;
    }
  }, []);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current !== undefined) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  }, []);

  const openNow = useCallback(() => {
    cancelScheduledOpen();
    cancelScheduledClose();
    setOpen(true);
    setHasOpened(true);
  }, [cancelScheduledOpen, cancelScheduledClose]);

  const scheduleOpen = useCallback(() => {
    cancelScheduledClose();
    cancelScheduledOpen();
    openTimerRef.current = window.setTimeout(() => {
      setOpen(true);
      setHasOpened(true);
    }, openDelayMs);
  }, [cancelScheduledClose, cancelScheduledOpen, openDelayMs]);

  // Defer closing so a pending close is cancelled if the pointer lands on the other
  // element (trigger <-> popper) within the grace period. Also abort any pending open,
  // so leaving the trigger before the open delay elapses won't pop the card.
  const scheduleClose = useCallback(() => {
    cancelScheduledOpen();
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), closeDelayMs);
  }, [cancelScheduledOpen, cancelScheduledClose, closeDelayMs]);

  useEffect(() => {
    if (anchorElement === null) {
      return;
    }

    if (forceInlineBlock) {
      // Intentional imperative mutation of the externally-owned host element inside an effect.
      // oxlint-disable-next-line react-hooks-js/immutability
      anchorElement.style.display = "inline-block";
    }

    anchorElement.addEventListener("mouseenter", scheduleOpen);
    anchorElement.addEventListener("mouseleave", scheduleClose);

    return () => {
      anchorElement.removeEventListener("mouseenter", scheduleOpen);
      anchorElement.removeEventListener("mouseleave", scheduleClose);
      cancelScheduledOpen();
      cancelScheduledClose();
    };
  }, [anchorElement, forceInlineBlock, scheduleOpen, scheduleClose, cancelScheduledOpen, cancelScheduledClose]);

  return { open, hasOpened, openNow, scheduleClose };
};
